import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
} from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { validateProductFeedbackScreenshot } from "../data/product-feedback-attachment";
import {
  PRODUCT_FEEDBACK_MESSAGE_MAX,
  PRODUCT_FEEDBACK_PROBLEM_KINDS,
  PRODUCT_FEEDBACK_TITLE_MAX,
  collectProductFeedbackSystemInfo,
  submitProductFeedback,
  validateProductFeedbackDraft,
  type ProductFeedbackEnvironment,
  type ProductFeedbackProblemKind,
  type ProductFeedbackSystemInfo,
  type ProductFeedbackSystemInfoKey,
  type ProductFeedbackType,
} from "../data/product-feedback";
import { getRecentClientErrorCodes } from "../lib/client-error-codes";
import { captureCurrentConsoleScreen } from "../lib/dom-snapshot";

const SYSTEM_INFO_LABELS: ReadonlyArray<{
  key: ProductFeedbackSystemInfoKey;
  label: string;
}> = [
  { key: "app_build", label: "App & build" },
  { key: "browser_device", label: "Browser & device" },
  { key: "page", label: "Where you were" },
  { key: "account_role", label: "Account & role" },
  { key: "session", label: "Session" },
  { key: "error_codes", label: "Recent technical errors" },
];

const PROBLEM_KIND_COPY: Record<ProductFeedbackProblemKind, string> = {
  broken: "Broken",
  unexpected: "Unexpected",
  slow: "Slow",
  confusing: "Confusing",
};

function browserEnvironment(): ProductFeedbackEnvironment {
  return {
    appVersion: typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.1.0",
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    // Query strings can contain reset tokens, emails, and other private identifiers. The route
    // pathname is enough to identify the Console surface without collecting those values.
    path: window.location.pathname,
    online: navigator.onLine,
    errorCodes: getRecentClientErrorCodes(),
  };
}

type ScreenshotChoice =
  | { readonly kind: "none" }
  | { readonly kind: "current-screen"; readonly file: File | null }
  | { readonly kind: "attachment"; readonly file: File };

function defaultScreenshotChoice(type: ProductFeedbackType): ScreenshotChoice {
  return type === "problem"
    ? { kind: "current-screen", file: null }
    : { kind: "none" };
}

function systemInfoDisplayValue(
  key: ProductFeedbackSystemInfoKey,
  value: string | readonly string[],
): string {
  if (key !== "error_codes" || !Array.isArray(value)) return String(value);
  return value.length === 0 ? "None captured in this tab" : value.join(" · ");
}

/**
 * Human-only Console feedback capture. The native dialog top layer supplies the browser focus
 * trap and background inerting; the existing Console drawer pattern supplies Escape/backdrop
 * close and the right-edge layout.
 */
export function ProductFeedbackSheet({
  client,
  accountId,
  accountName,
  roleLabel,
  pageLabel,
  initialType,
  onClose,
  environment,
}: {
  client: SupabaseClient;
  accountId: string;
  accountName: string;
  roleLabel: string;
  pageLabel: string;
  initialType: ProductFeedbackType;
  onClose: () => void;
  environment?: ProductFeedbackEnvironment;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const systemInfoToggleRef = useRef<HTMLInputElement>(null);
  const [type, setType] = useState<ProductFeedbackType>(initialType);
  const [problemKinds, setProblemKinds] = useState<ReadonlySet<ProductFeedbackProblemKind>>(
    new Set(),
  );
  const [includedInfo, setIncludedInfo] = useState<ReadonlySet<ProductFeedbackSystemInfoKey>>(
    new Set(SYSTEM_INFO_LABELS.map(({ key }) => key)),
  );
  const [reviewInfo, setReviewInfo] = useState(false);
  const [screenshotChoice, setScreenshotChoice] = useState<ScreenshotChoice>(() =>
    defaultScreenshotChoice(initialType),
  );
  const [screenshotPreviewUrl, setScreenshotPreviewUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocker, setBlocker] = useState<string | null>(null);
  const [received, setReceived] = useState(false);

  const systemInfoValues = useMemo(
    () =>
      collectProductFeedbackSystemInfo({
        environment: environment ?? browserEnvironment(),
        accountName,
        roleLabel,
        pageLabel,
      }),
    [accountName, environment, pageLabel, roleLabel],
  );

  const selectedScreenshotFile =
    screenshotChoice.kind === "none" ? null : screenshotChoice.file;
  const locked = busy || capturing;

  useEffect(() => {
    if (selectedScreenshotFile === null || typeof URL.createObjectURL !== "function") {
      setScreenshotPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedScreenshotFile);
    setScreenshotPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedScreenshotFile]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog !== null && !dialog.open) dialog.showModal();
  }, []);

  function requestClose(): void {
    if (!locked) onClose();
  }

  function chooseType(nextType: ProductFeedbackType): void {
    setType(nextType);
    setScreenshotChoice((current) =>
      current.kind === "attachment" ? current : defaultScreenshotChoice(nextType),
    );
    setBlocker(null);
  }

  function chooseScreenshotFile(file: File): void {
    const validation = validateProductFeedbackScreenshot(file);
    if (!validation.ok) {
      setBlocker(validation.reason);
      if (fileInputRef.current !== null) fileInputRef.current.value = "";
      return;
    }
    setScreenshotChoice({ kind: "attachment", file });
    setBlocker(null);
  }

  function handlePaste(event: ClipboardEvent<HTMLFormElement>): void {
    const image = [...event.clipboardData.files].find((file) =>
      file.type.startsWith("image/"),
    );
    if (image === undefined) return;
    event.preventDefault();
    chooseScreenshotFile(image);
  }

  async function previewCurrentScreen(): Promise<void> {
    if (locked) return;
    setCapturing(true);
    setBlocker(null);
    try {
      const file = await captureCurrentConsoleScreen();
      setScreenshotChoice({ kind: "current-screen", file });
    } catch (error) {
      setBlocker(
        error instanceof Error
          ? error.message
          : "We couldn't capture this screen. Turn it off or attach a PNG or JPEG instead.",
      );
    } finally {
      setCapturing(false);
    }
  }

  function toggleProblemKind(kind: ProductFeedbackProblemKind): void {
    setProblemKinds((current) => {
      const next = new Set(current);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
    setBlocker(null);
  }

  function toggleSystemInfo(key: ProductFeedbackSystemInfoKey): void {
    setIncludedInfo((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setBlocker(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (locked || received || formRef.current === null) return;

    const formData = new FormData(formRef.current);
    const systemInfo = SYSTEM_INFO_LABELS.reduce<ProductFeedbackSystemInfo>(
      (info, item) =>
        includedInfo.has(item.key)
          ? { ...info, [item.key]: systemInfoValues[item.key] }
          : info,
      {},
    );
    const draft = {
      type,
      problemKinds: [...problemKinds],
      title: String(formData.get("idea_title") ?? ""),
      message: String(
        formData.get(type === "idea" ? "idea_message" : "problem_message") ?? "",
      ),
      systemInfo,
      allowContact: formData.get("allow_contact") === "on",
    };
    const validation = validateProductFeedbackDraft(draft, {
      hasScreenshot: screenshotChoice.kind !== "none",
    });
    if (!validation.ok) {
      setBlocker(validation.reason);
      return;
    }

    setBusy(true);
    setBlocker(null);
    let screenshot = selectedScreenshotFile;
    if (screenshotChoice.kind === "current-screen" && screenshot === null) {
      try {
        screenshot = await captureCurrentConsoleScreen();
        setScreenshotChoice({ kind: "current-screen", file: screenshot });
      } catch (error) {
        setBusy(false);
        setBlocker(
          error instanceof Error
            ? error.message
            : "We couldn't capture this screen. Turn it off or attach a PNG or JPEG instead.",
        );
        return;
      }
    }
    const outcome = await submitProductFeedback(client, accountId, draft, screenshot);
    setBusy(false);
    if (!outcome.ok) {
      setBlocker(outcome.reason);
      return;
    }
    setReceived(true);
  }

  const allSystemInfoIncluded = includedInfo.size === SYSTEM_INFO_LABELS.length;

  useEffect(() => {
    if (systemInfoToggleRef.current !== null) {
      systemInfoToggleRef.current.indeterminate =
        includedInfo.size > 0 && !allSystemInfoIncluded;
    }
  }, [allSystemInfoIncluded, includedInfo.size]);

  return (
    <dialog
      ref={dialogRef}
      className="pfs-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pfs-title"
      aria-describedby={received ? "pfs-description" : "pfs-description pfs-disclosure"}
      data-html2canvas-ignore="true"
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) requestClose();
      }}
    >
      <div className="pfs-sheet__frame">
        {received ? (
          <div className="pfs-receipt" role="status">
            <span className="pfs-receipt__check" aria-hidden="true">
              ✓
            </span>
            <h2 id="pfs-title">We received it.</h2>
            <p id="pfs-description">
              Thanks for flagging it. A person on the SecOps team reads every report—no
              automated replies.
            </p>
            <button
              type="button"
              className="pfs-button pfs-button--primary"
              onClick={requestClose}
            >
              Done
            </button>
          </div>
        ) : (
          <form
            ref={formRef}
            className="pfs-form"
            onPaste={handlePaste}
            onSubmit={(event) => void handleSubmit(event)}
          >
            <header className="pfs-sheet__head">
              <h2 id="pfs-title">{type === "idea" ? "Share an idea" : "Report a problem"}</h2>
              <button
                type="button"
                className="pfs-sheet__close"
                aria-label="Close"
                disabled={locked}
                onClick={requestClose}
              >
                ×
              </button>
            </header>

            <div className="pfs-segment" role="radiogroup" aria-label="Feedback type">
              <button
                type="button"
                role="radio"
                aria-checked={type === "problem"}
                disabled={locked}
                onClick={() => {
                  chooseType("problem");
                }}
              >
                Problem
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={type === "idea"}
                disabled={locked}
                onClick={() => {
                  chooseType("idea");
                }}
              >
                Idea
              </button>
            </div>

            <div className="pfs-sheet__body">
              <p id="pfs-description" className="pfs-intro">
                {type === "idea"
                  ? "What would make this work better for your team? A sentence is plenty."
                  : "Tell us what happened. We'll include session details so you don't have to retype technical info—you can review or remove them below."}
              </p>

              <div id="pfs-disclosure" className="pfs-disclosure">
                <strong>What will be sent</strong>
                <span>
                  Your note, the system categories left checked below, and one screenshot when
                  enabled. Current-screen capture starts on for problems and leaves this feedback
                  sheet out; attached or pasted images are sent exactly as selected. Screenshots
                  can contain visible private information, so preview, replace, or turn them off
                  before sending.
                </span>
              </div>

              <section className="pfs-block" hidden={type !== "problem"}>
                <h3>
                  What kind of problem? <span>· optional</span>
                </h3>
                <div className="pfs-kinds" aria-label="Problem kind">
                  {PRODUCT_FEEDBACK_PROBLEM_KINDS.map((kind) => (
                    <button
                      key={kind}
                      type="button"
                      aria-pressed={problemKinds.has(kind)}
                      disabled={locked}
                      onClick={() => toggleProblemKind(kind)}
                    >
                      {PROBLEM_KIND_COPY[kind]}
                    </button>
                  ))}
                </div>
              </section>

              <section className="pfs-block" hidden={type !== "idea"}>
                <label htmlFor="pfs-idea-title">Your idea</label>
                <input
                  id="pfs-idea-title"
                  name="idea_title"
                  type="text"
                  maxLength={PRODUCT_FEEDBACK_TITLE_MAX}
                  placeholder="Give it a short name"
                  disabled={locked}
                />
                <label className="pfs-visually-hidden" htmlFor="pfs-idea-message">
                  What would this idea let you do? Optional
                </label>
                <textarea
                  id="pfs-idea-message"
                  name="idea_message"
                  maxLength={PRODUCT_FEEDBACK_MESSAGE_MAX}
                  placeholder="Optional—what would this let you do?"
                  disabled={locked}
                />
              </section>

              <section className="pfs-block" hidden={type !== "problem"}>
                <label htmlFor="pfs-problem-message">
                  What happened? <span>· optional</span>
                </label>
                <textarea
                  id="pfs-problem-message"
                  name="problem_message"
                  maxLength={PRODUCT_FEEDBACK_MESSAGE_MAX}
                  placeholder="What did you expect, and what did you see instead?"
                  disabled={locked}
                />
              </section>

              <section className="pfs-block pfs-screenshot" aria-labelledby="pfs-shot-title">
                <h3 id="pfs-shot-title">
                  Screenshot <span>· optional · one image</span>
                </h3>
                <div className="pfs-screenshot__card">
                  <label className="pfs-screenshot__current">
                    <input
                      type="checkbox"
                      checked={screenshotChoice.kind === "current-screen"}
                      disabled={locked}
                      onChange={(event) => {
                        setScreenshotChoice(
                          event.currentTarget.checked
                            ? { kind: "current-screen", file: null }
                            : { kind: "none" },
                        );
                        setBlocker(null);
                      }}
                    />
                    <span>
                      Include current screen
                      <small>
                        {screenshotChoice.kind === "current-screen"
                          ? "On — captured from this tab when you send"
                          : "Off — no screen capture will be sent"}
                      </small>
                    </span>
                  </label>
                  <div className="pfs-screenshot__actions">
                    <button
                      type="button"
                      disabled={locked}
                      onClick={() => void previewCurrentScreen()}
                    >
                      {capturing ? "Capturing…" : "Preview current screen"}
                    </button>
                    <button
                      type="button"
                      disabled={locked}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose PNG or JPEG
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    className="pfs-visually-hidden"
                    type="file"
                    accept="image/png,image/jpeg"
                    aria-label="Choose screenshot file"
                    disabled={locked}
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      if (file !== undefined) chooseScreenshotFile(file);
                    }}
                  />
                  <p className="pfs-screenshot__hint">
                    You can also paste an image anywhere in this sheet. PNG or JPEG, up to 3 MB.
                  </p>
                  {screenshotPreviewUrl !== null && selectedScreenshotFile !== null ? (
                    <div className="pfs-screenshot__preview">
                      <img src={screenshotPreviewUrl} alt="Screenshot preview" />
                      <span>
                        {screenshotChoice.kind === "current-screen"
                          ? "Current screen preview"
                          : selectedScreenshotFile.name}
                        <small>
                          {(selectedScreenshotFile.size / 1024).toFixed(0)} KB ·{" "}
                          {selectedScreenshotFile.type}
                        </small>
                      </span>
                      <button
                        type="button"
                        aria-label="Remove screenshot"
                        disabled={locked}
                        onClick={() => {
                          setScreenshotChoice({ kind: "none" });
                          if (fileInputRef.current !== null) fileInputRef.current.value = "";
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : null}
                </div>
              </section>

              <section className="pfs-block">
                <h3>Included with your report</h3>
                <div className="pfs-system">
                  <div className="pfs-system__head">
                    <label>
                      <input
                        ref={systemInfoToggleRef}
                        type="checkbox"
                        checked={allSystemInfoIncluded}
                        disabled={locked}
                        onChange={() => {
                          setIncludedInfo(
                            allSystemInfoIncluded
                              ? new Set()
                              : new Set(SYSTEM_INFO_LABELS.map(({ key }) => key)),
                          );
                          setBlocker(null);
                        }}
                      />
                      <span>
                        Include system info
                        <small>So you don't have to retype technical details</small>
                      </span>
                    </label>
                    <button
                      type="button"
                      aria-expanded={reviewInfo}
                      disabled={locked}
                      onClick={() => setReviewInfo((open) => !open)}
                    >
                      {reviewInfo ? "Hide" : "Review"}
                    </button>
                  </div>

                  {reviewInfo ? (
                    <div className="pfs-system__detail">
                      {SYSTEM_INFO_LABELS.map((item) => (
                        <label key={item.key}>
                          <input
                            type="checkbox"
                            checked={includedInfo.has(item.key)}
                            disabled={locked}
                            onChange={() => toggleSystemInfo(item.key)}
                          />
                          <span>
                            <strong>{item.label}</strong>
                            <small>
                              {systemInfoDisplayValue(item.key, systemInfoValues[item.key])}
                            </small>
                          </span>
                        </label>
                      ))}
                      <p>
                        System info never includes passwords, tokens, precise location, error
                        messages, stacks, or request/response bodies. Recent errors are names/codes
                        only.
                      </p>
                    </div>
                  ) : null}
                </div>
              </section>

              <label className="pfs-contact">
                <input name="allow_contact" type="checkbox" disabled={locked} />
                You may contact me about this
              </label>
            </div>

            <footer className="pfs-sheet__foot">
              <div className="pfs-sheet__actions">
                <button
                  type="submit"
                  className="pfs-button pfs-button--primary"
                  disabled={locked}
                >
                  {capturing ? "Capturing…" : busy ? "Sending…" : "Send"}
                </button>
                <button
                  type="button"
                  className="pfs-button"
                  disabled={locked}
                  onClick={requestClose}
                >
                  Cancel
                </button>
              </div>
              <p>
                For <strong>{accountName}</strong> · as <strong>{roleLabel}</strong>—a person reads
                every report.
              </p>
              {blocker !== null ? (
                <p className="pfs-sheet__blocker" role="alert">
                  {blocker}
                </p>
              ) : null}
            </footer>
          </form>
        )}
      </div>
    </dialog>
  );
}
